import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';
import React, { useCallback, useMemo, useState } from 'react';
import { LuCheck, LuCopy, LuMail } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/utils/cn';

import { SourceGuid } from './SourceGuid';

export const md = new MarkdownIt({ breaks: false, linkify: true });

export const PROSE_CLASSES =
    'prose prose-sm prose-p:my-2.5 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-headings:my-3 prose-h2:mt-6 prose-h3:mt-4 prose-h2:mb-1 prose-h3:mb-1 prose-strong:font-semibold max-w-none break-words';

const TABLE_PROSE_CLASSES =
    'prose prose-sm prose-table:text-xs prose-td:px-3 prose-td:py-1.5 prose-th:px-3 prose-th:py-1.5 prose-thead:bg-muted/60 prose-tr:border-border prose-table:border-border prose-table:border max-w-none';

function splitHtmlAtTables(html: string): Array<{ type: 'html' | 'table'; content: string }> {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const parts: Array<{ type: 'html' | 'table'; content: string }> = [];
    let currentHtml = '';

    for (const node of Array.from(doc.body.childNodes)) {
        if ((node as Element).tagName === 'TABLE') {
            if (currentHtml) {
                parts.push({ type: 'html', content: currentHtml });
                currentHtml = '';
            }
            parts.push({ type: 'table', content: (node as Element).outerHTML });
        } else {
            currentHtml += (node as Element).outerHTML ?? node.textContent ?? '';
        }
    }
    if (currentHtml) parts.push({ type: 'html', content: currentHtml });
    return parts;
}

const TableCard: React.FC<{ html: string }> = ({ html }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <div className="border-border bg-background my-3 overflow-hidden rounded-xl border">
                <div className="max-h-[220px] overflow-x-auto overflow-y-hidden">
                    <div className={cn(TABLE_PROSE_CLASSES, 'px-3 pt-3')} dangerouslySetInnerHTML={{ __html: html }} />
                </div>
                <div className="from-background pointer-events-none -mt-16 h-16 bg-gradient-to-t to-transparent" />
                <div className="border-border border-t">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground w-full rounded-none text-xs"
                        onClick={() => setIsOpen(true)}
                    >
                        View full table
                    </Button>
                </div>
            </div>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="max-h-[80vh] max-w-[90vw] overflow-auto">
                    <DialogTitle className="sr-only">Table</DialogTitle>
                    <div
                        className={cn(TABLE_PROSE_CLASSES, 'overflow-x-auto')}
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

const CITATION_PATTERN_SOURCE = String.raw`\[(NOTE|EMAIL|TEXT_MESSAGE):\s*([0-9a-f-]+):\s*([0-9a-f-]+)\]`;

type CitationType = 'NOTE' | 'EMAIL' | 'TEXT_MESSAGE';

interface Citation {
    type: CitationType;
    householdGUID: string;
    entityGUID: string;
}

const CITATION_CONFIG: Record<
    CitationType,
    { label: string; dotColor: 'green' | 'blue' | 'amber'; path: (householdGUID: string, entityGUID: string) => string }
> = {
    NOTE: {
        label: 'NOTE',
        dotColor: 'blue',
        path: (householdGUID, entityGUID) => `/household/${householdGUID}/notes/view?noteGUID=${entityGUID}`,
    },
    EMAIL: {
        label: 'EMAIL',
        dotColor: 'amber',
        path: (householdGUID, entityGUID) => `/household/${householdGUID}/emails/${entityGUID}`,
    },
    TEXT_MESSAGE: {
        label: 'TEXT',
        dotColor: 'green',
        path: (householdGUID, entityGUID) => `/household/${householdGUID}/texts/thread?msg=${entityGUID}`,
    },
};

function extractSources(content: string): { body: string; citations: Citation[] } {
    const sourcesIndex = content.search(/^##\s*Sources\s*$/im);
    if (sourcesIndex === -1) return { body: content, citations: [] };

    const body = content.slice(0, sourcesIndex).trimEnd();
    const sourcesBlock = content.slice(sourcesIndex);
    const citations: Citation[] = [];
    const re = new RegExp(CITATION_PATTERN_SOURCE, 'gi');
    let match: RegExpExecArray | null;
    while ((match = re.exec(sourcesBlock)) !== null) {
        citations.push({
            type: match[1].toUpperCase() as CitationType,
            householdGUID: match[2],
            entityGUID: match[3],
        });
    }
    return { body, citations };
}

const SourcesSection: React.FC<{ citations: Citation[] }> = ({ citations }) => {
    const navigate = useNavigate();
    if (citations.length === 0) return null;
    return (
        <div className="border-border mt-4 border-t pt-3">
            <p className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">Sources</p>
            <div className="flex flex-wrap gap-2">
                {citations.map((c, i) => {
                    const { label, dotColor, path } = CITATION_CONFIG[c.type];
                    return (
                        <SourceGuid
                            key={i}
                            guid={c.entityGUID}
                            label={label}
                            dotColor={dotColor}
                            onClick={() => navigate(path(c.householdGUID, c.entityGUID))}
                        />
                    );
                })}
            </div>
        </div>
    );
};

interface EmailDraft {
    to: string;
    subject: string;
    body: string;
}

function parseEmailDraft(raw: string): EmailDraft | null {
    const lines = raw.replace(/\r\n/g, '\n').split('\n');
    let toLine = '';
    let subjectLine = '';
    let bodyStartIndex = -1;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!toLine && /^to:/i.test(line)) {
            toLine = line.replace(/^to:\s*/i, '').trim();
        } else if (!subjectLine && /^subject:/i.test(line)) {
            subjectLine = line.replace(/^subject:\s*/i, '').trim();
        } else if (toLine && subjectLine && line.trim() === '' && bodyStartIndex === -1) {
            bodyStartIndex = i + 1;
            break;
        }
    }

    if (!toLine || !subjectLine) return null;
    const body = bodyStartIndex >= 0 ? lines.slice(bodyStartIndex).join('\n').trim() : '';
    return { to: toLine, subject: subjectLine, body };
}

function parseRecipients(to: string): string[] {
    return to
        .split(',')
        .map((r) => {
            const match = r.trim().match(/^([^<]+)<[^>]+>$/);
            return match ? match[1].trim() : r.trim();
        })
        .filter(Boolean);
}

type ContentSegment = { type: 'markdown'; content: string } | { type: 'email'; draft: EmailDraft };

function splitEmailDrafts(content: string): ContentSegment[] {
    const EMAIL_FENCE_RE = /```email\n([\s\S]*?)```/g;
    const segments: ContentSegment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = EMAIL_FENCE_RE.exec(content)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: 'markdown', content: content.slice(lastIndex, match.index) });
        }
        const draft = parseEmailDraft(match[1]);
        if (draft) {
            segments.push({ type: 'email', draft });
        } else {
            segments.push({ type: 'markdown', content: match[0] });
        }
        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
        segments.push({ type: 'markdown', content: content.slice(lastIndex) });
    }

    return segments;
}

const EmailDraftCard: React.FC<{ draft: EmailDraft }> = ({ draft }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const recipients = useMemo(() => parseRecipients(draft.to), [draft.to]);

    const handleCopy = useCallback(() => {
        const html = DOMPurify.sanitize(md.render(draft.body));
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const plainText = tmp.innerText;
        const onCopied = () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        };
        if (typeof ClipboardItem !== 'undefined') {
            const htmlBlob = new Blob([html], { type: 'text/html' });
            const textBlob = new Blob([plainText], { type: 'text/plain' });
            navigator.clipboard
                .write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })])
                .then(onCopied);
        } else {
            navigator.clipboard.writeText(plainText).then(onCopied);
        }
    }, [draft.body]);

    return (
        <>
            <div className="border-border bg-background my-3 overflow-hidden rounded-xl border">
                <div className="border-border border-b px-4 pt-4 pb-3">
                    <p className="text-muted-foreground text-xs font-medium">
                        {recipients.length} {recipients.length === 1 ? 'Recipient' : 'Recipients'}
                    </p>
                </div>
                <div className="border-border border-b px-4 py-3">
                    <p className="text-foreground text-sm">
                        <span className="font-semibold">Subject:</span> {draft.subject}
                    </p>
                </div>
                <div className="relative px-4 pt-3 pb-1">
                    <p className="text-foreground line-clamp-4 text-sm leading-relaxed whitespace-pre-wrap">
                        {draft.body}
                    </p>
                    <div className="from-background pointer-events-none mt-1 h-8 bg-gradient-to-t to-transparent" />
                </div>
                <div className="border-border border-t">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground w-full rounded-none text-xs"
                        onClick={() => setIsOpen(true)}
                    >
                        <LuMail className="mr-1.5 h-3.5 w-3.5" />
                        Review
                    </Button>
                </div>
            </div>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="max-h-[80vh] max-w-lg overflow-auto">
                    <DialogTitle className="sr-only">Email Draft</DialogTitle>
                    <div className="flex flex-col gap-4">
                        <div className="flex items-center justify-between pr-6">
                            <div className="flex items-center gap-2">
                                <LuMail className="text-muted-foreground h-4 w-4 flex-shrink-0" />
                                <span className="text-foreground text-sm font-semibold">Email Draft</span>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-foreground gap-1.5 text-xs"
                                onClick={handleCopy}
                            >
                                {copied ? (
                                    <LuCheck className="h-3.5 w-3.5 text-green-500" />
                                ) : (
                                    <LuCopy className="h-3.5 w-3.5" />
                                )}
                                {copied ? 'Copied!' : 'Copy body'}
                            </Button>
                        </div>
                        <div className="border-border rounded-lg border">
                            <div className="border-border border-b px-4 py-2.5">
                                <p className="text-muted-foreground text-xs">
                                    <span className="font-medium">To:</span> {draft.to}
                                </p>
                            </div>
                            <div className="border-border border-b px-4 py-2.5">
                                <p className="text-foreground text-sm">
                                    <span className="font-semibold">Subject:</span> {draft.subject}
                                </p>
                            </div>
                            <div className="px-4 py-3">
                                <div
                                    className={PROSE_CLASSES}
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(md.render(draft.body)),
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export const RenderedMarkdown: React.FC<{ content: string }> = ({ content }) => {
    const { body, citations } = useMemo(() => extractSources(content), [content]);
    const segments = useMemo(() => splitEmailDrafts(body), [body]);

    return (
        <div className="min-w-0 overflow-hidden">
            {segments.map((seg, i) => {
                if (seg.type === 'email') {
                    return <EmailDraftCard key={i} draft={seg.draft} />;
                }
                const html = DOMPurify.sanitize(md.render(seg.content));
                const parts = splitHtmlAtTables(html);
                return parts.map((part, j) =>
                    part.type === 'table' ? (
                        <TableCard key={`${i}-${j}`} html={part.content} />
                    ) : (
                        <div
                            key={`${i}-${j}`}
                            className={PROSE_CLASSES}
                            dangerouslySetInnerHTML={{ __html: part.content }}
                        />
                    ),
                );
            })}
            <SourcesSection citations={citations} />
        </div>
    );
};
